import config from '@/config';
import axiosInstance from '@/school-management/utils/api';
import { AxiosError } from 'axios';
import {
  ClassReportContent,
  StudentMetricsDashboardContent,
  StudentMetricsDashboardFilters,
  StudentSkillPerformanceContent,
  StudentSkillPerformanceQueryParams,
} from '@/school-management/types/generate-report';

export interface ReportServiceError {
  code: string;
  message: string;
  statusCode?: number;
  details?: any;
  url?: string;
}

const createReportServiceError = (error: any): ReportServiceError => {
  const axiosError = error as AxiosError;
  const status = axiosError.response?.status || 500;
  const responseData = axiosError.response?.data as any;

  const message = responseData?.message || axiosError.message || 'An unexpected error occurred';
  const code = responseData?.code || `HTTP_${status}`;

  const url = axiosError.config?.url;

  const reportServiceError: ReportServiceError = {
    code,
    message,
    statusCode: status,
    url,
    details: responseData?.details || responseData,
  };

  return reportServiceError;
};

export interface ReportService {
  fetchClassReport: (
    groupId: string,
    schoolId: string
  ) => Promise<{ data: { data: ClassReportContent } } | null>;
  fetchStudentMetricsDashboard: (
    filters: StudentMetricsDashboardFilters
  ) => Promise<{ data: { data: StudentMetricsDashboardContent } } | null>;
  fetchStudentSkillPerformance: (
    filters: StudentSkillPerformanceQueryParams
  ) => Promise<{ data: { data: StudentSkillPerformanceContent } } | null>;
}

export const reportService: ReportService = {
  fetchClassReport: async (
    groupId: string,
    schoolId: string
  ): Promise<{ data: { data: ClassReportContent } } | null> => {
    try {
      const url = `/admin-management/${config.apiVersion}/${schoolId}/groups/reports/${groupId}`;

      const response = await axiosInstance.get(url);
      const payload: any = response?.data;

      if (
        payload &&
        payload.status === 'error' &&
        (payload.message || '').toLowerCase() === 'class report not found'
      ) {
        throw {
          code: 'HTTP_404',
          message: payload.message,
          statusCode: 404,
          url,
          details: payload,
        } as ReportServiceError;
      }

      return response;
    } catch (error: any) {
      const reportServiceError = createReportServiceError(error);
      console.error('Error fetching class report:', reportServiceError);

      throw reportServiceError;
    }
  },
  fetchStudentMetricsDashboard: async (
    filters: StudentMetricsDashboardFilters
  ): Promise<{ data: { data: StudentMetricsDashboardContent } } | null> => {
    try {
      const url = `/admin-management/${config.apiVersion}/metrics/student-metrics-dashboard`;

      const response = await axiosInstance.post(url, filters);
      const payload: any = response?.data;

      if (
        payload &&
        payload.status === 'error' &&
        (payload.message || '').toLowerCase() === 'user organization details not found'
      ) {
        throw {
          code: 'HTTP_400',
          message: payload.message,
          statusCode: 400,
          url,
          details: payload,
        } as ReportServiceError;
      }

      return response;
    } catch (error: any) {
      const reportServiceError = createReportServiceError(error);
      console.error('Error fetching student metrics dashboard:', reportServiceError);

      throw reportServiceError;
    }
  },
  fetchStudentSkillPerformance: async (
    filters: StudentSkillPerformanceQueryParams
  ): Promise<{ data: { data: StudentSkillPerformanceContent } } | null> => {
    try {
      const url = `/admin-management/${config.apiVersion}/student-skill-performance`;

      const response = await axiosInstance.post(url, filters);
      const payload: any = response?.data;

      if (
        payload &&
        payload.status === 'error' &&
        (payload.message || '').toLowerCase() === 'user organization details not found'
      ) {
        throw {
          code: 'HTTP_400',
          message: payload.message,
          statusCode: 400,
          url,
          details: payload,
        } as ReportServiceError;
      }

      return response;
    } catch (error: any) {
      const reportServiceError = createReportServiceError(error);
      console.error('Error fetching student skill performance:', reportServiceError);

      throw reportServiceError;
    }
  },
};
